import { type Query, useInfiniteQuery, useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { notification } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import {
  deleteCatalogGame,
  getCatalogGameMappings,
  getCatalogGames,
  getCatalogGamesCreationResult,
  getCatalogGamesCreationResults,
  getCatalogGamesFilters,
  getCatalogGamesSegments,
  triggerCreationResults,
} from 'src/api/cms';
import { showMessage } from 'src/services/notify/notify';
import { queryClient } from 'src/services/react-query/query-client';
import { useCatalogGamesStore } from 'src/stores/catalog-games';
import type { CatalogGamesSegmentsFilters, GetCatalogGameCreationResultResponse } from 'src/types/catalog-games';
import { useShallow } from 'zustand/shallow';

const useCatalogGamesState = useCatalogGamesStore.getState();

export const useCatalogGamesFilters = () =>
  useCatalogGamesStore(
    useShallow((state) => ({
      ...state.filters,
    })),
  );

export const useCatalogGamesGameModal = () =>
  useCatalogGamesStore(
    useShallow((state) => ({
      ...state.gameModal,
    })),
  );

export const useCatalogGamesCreationResultIds = () => useCatalogGamesStore((state) => state.creationResultIds);

export const useCatalogGamesActions = () => ({
  setFilters: useCatalogGamesState.setFilters,
  setInitialFilters: useCatalogGamesState.setInitialFilters,
  setGameModal: useCatalogGamesState.setGameModal,
  addCreationResultIds: useCatalogGamesState.addCreationResultIds,
  removeCreationResultIds: useCatalogGamesState.removeCreationResultIds,
});

export const useGetCatalogGamesFiltersData = () => {
  const { data, isPending } = useQuery({
    queryKey: ['catalogGames', 'filters'],
    queryFn: () => getCatalogGamesFilters(),
  });

  return {
    data,
    isInProgress: isPending,
  };
};

export const useSearchCoincidences = (filters: { gameNameQuery: string; gameProviderNameQuery: string }) => {
  return useInfiniteQuery({
    queryKey: ['catalogGames', 'coincidences', filters],
    queryFn: async ({ pageParam }) => getCatalogGames({ pagination: { page: pageParam, limit: 100 }, filters }),
    getNextPageParam: (lastPage) => (lastPage.pagination.hasNextPage ? lastPage.pagination.page + 1 : null),
    initialPageParam: 1,
    enabled: !!filters.gameNameQuery || !!filters.gameProviderNameQuery,
  });
};

export const useGetCatalogGamesData = (enabled = true) => {
  const filters = useCatalogGamesFilters();

  return useInfiniteQuery({
    queryKey: ['catalogGames', filters],
    queryFn: async ({ pageParam }) => getCatalogGames({ pagination: { page: pageParam, limit: 100 }, filters }),
    getNextPageParam: (lastPage) => (lastPage.pagination.hasNextPage ? lastPage.pagination.page + 1 : null),
    initialPageParam: 1,
    enabled,
  });
};

export const useGetCatalogGameMappingsData = (id: number, enabled = true) => {
  const { data, isPending } = useQuery({
    queryKey: ['catalogGames', 'mappings', id],
    queryFn: async () => {
      try {
        return await getCatalogGameMappings(id);
      } catch (error) {
        showMessage({
          type: 'error',
          content: 'Failed to load game mappings',
        });

        throw error;
      }
    },
    enabled,
  });

  return {
    data,
    isInProgress: isPending,
  };
};

export const useGetCatalogGamesSegmentsData = (filters: CatalogGamesSegmentsFilters) => {
  return useInfiniteQuery({
    queryKey: ['catalogGames', 'segments', filters],
    queryFn: async ({ pageParam }) => getCatalogGamesSegments({ pagination: { page: pageParam, limit: 100 }, filters }),
    getNextPageParam: (lastPage) => (lastPage.pagination.hasNextPage ? lastPage.pagination.page + 1 : null),
    initialPageParam: 1,
    enabled: !!filters.length,
  });
};

export const useTriggerCreationResultsAction = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: triggerCreationResults,
  });

  return {
    triggerCreationResults: mutateAsync,
    isInProgress: isPending,
  };
};

export const useDeleteCatalogGameAction = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: deleteCatalogGame,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['catalogGames', useCatalogGamesStore.getState().filters] }),
  });

  return {
    deleteCatalogGame: mutateAsync,
    isInProgress: isPending,
  };
};

const useCatalogGamesInitialCreationResults = () => {
  const { addCreationResultIds } = useCatalogGamesActions();
  const [isChecked, setIsChecked] = useState(false);
  const { data } = useQuery({
    queryKey: ['catalogGames', 'creationResults'],
    queryFn: getCatalogGamesCreationResults,
    gcTime: Infinity,
  });

  useEffect(() => {
    if (!isChecked && data && data.length > 0) {
      addCreationResultIds(data.filter((result) => result.status === 'running').map((result) => result.id));
      setIsChecked(true);
    }
  }, [addCreationResultIds, data, isChecked]);
};

export const useCatalogGamesCreationResultsPolling = () => {
  const creationResultIds = useCatalogGamesCreationResultIds();
  const { removeCreationResultIds } = useCatalogGamesActions();
  const notifiedIdsRef = useRef<string[]>([]);

  useCatalogGamesInitialCreationResults();

  const queries = useQueries({
    queries: creationResultIds.map((id) => ({
      queryKey: ['catalogGames', 'creationResult', id],
      queryFn: () => getCatalogGamesCreationResult(id),
      refetchInterval: (query: Query<GetCatalogGameCreationResultResponse, Error>) => {
        if (!query) {
          return false;
        }

        return query.state.data?.status === 'running' ? 15000 : false;
      },
      enabled: !!id,
    })),
  });

  useEffect(() => {
    const completedIds: string[] = [];

    queries.forEach((query) => {
      if (!query.data) {
        return;
      }

      if (query.data.status === 'not running' && !notifiedIdsRef.current.includes(query.data.id)) {
        const isCreate = query.data.type === 'create';
        notifiedIdsRef.current.push(query.data.id);
        completedIds.push(query.data.id);

        notification.open({
          description: `"${query.data.gameName}" ${isCreate ? 'created' : 'edited'} ${dayjs.utc(query.data.updatedAt).format('DD MMM HH:mm')}`,
          title: `Game ${isCreate ? 'Creation' : 'Editing'} Results`,
          duration: 2,
        });
      }
    });

    if (completedIds.length > 0) {
      removeCreationResultIds(completedIds);
    }
  }, [queries, removeCreationResultIds]);
};
